import type { ReactNode } from 'react';

import { DataCard, type DataCardField } from '@/components/ui/data-card';

export interface DataCardFieldConfig<TData> {
  label: string;
  render: (row: TData) => ReactNode;
  className?: string;
  /** Return false to hide this field for a given row. Default: always shown. */
  visible?: (row: TData) => boolean;
}

export interface DataCardConfig<TData> {
  header: (row: TData) => ReactNode;
  trailing?: (row: TData) => ReactNode;
  fields: DataCardFieldConfig<TData>[];
  footer?: (row: TData) => ReactNode;
  onClick?: (row: TData) => void;
  getState?: (row: TData) => { selected?: boolean; highlighted?: boolean };
}

export interface DataCardListProps<TData> {
  data: TData[];
  getRowId: (row: TData) => string;
  cardConfig: DataCardConfig<TData>;
  emptyMessage?: string;
  striped?: boolean;
  className?: string;
}

export function DataCardList<TData>({
  data,
  getRowId,
  cardConfig,
  emptyMessage = 'No results.',
  striped,
  className,
}: DataCardListProps<TData>) {
  if (data.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={className ?? 'space-y-3'}>
      {data.map((row, index) => {
        const state = cardConfig.getState?.(row) ?? {};
        const fields: DataCardField[] = cardConfig.fields
          .filter((field) => (field.visible ? field.visible(row) : true))
          .map((field) => ({
            label: field.label,
            value: field.render(row),
            className: field.className,
          }));

        return (
          <DataCard
            key={getRowId(row)}
            index={index}
            striped={striped}
            header={cardConfig.header(row)}
            trailing={cardConfig.trailing?.(row)}
            fields={fields}
            footer={cardConfig.footer?.(row)}
            onClick={cardConfig.onClick ? () => cardConfig.onClick?.(row) : undefined}
            selected={state.selected}
            highlighted={state.highlighted}
          />
        );
      })}
    </div>
  );
}
